import { ArrowLeftIcon, BellIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import NotificationActionMenu from '../../components/NotificationActionMenu';
import NotificationFilterTabs from '../../components/NotificationFilterTabs';
import { useNotifications } from '../../hooks/useNotifications';

export default function NotificationsPage() {
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');

  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore,
    hasMore,
    isDeleting
  } = useNotifications();

  const filteredNotifications = (notifications || []).filter((notification) => {
    if (filter === 'unread') return notification.status === 'unread';
    if (filter === 'read') return notification.status === 'read';
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Link
              to="/"
              className="rounded-full p-2 transition-colors hover:bg-gray-100"
            >
              <ArrowLeftIcon className="h-6 w-6 text-gray-600" />
            </Link>
            <div className="flex items-center space-x-2">
              <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="rounded-lg border border-orange-200 px-3 py-1 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50 hover:text-orange-700"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <NotificationFilterTabs
          filter={filter}
          onFilterChange={setFilter}
          allCount={(notifications || []).length}
          unreadCount={unreadCount || 0}
          readCount={(notifications || []).length - (unreadCount || 0)}
        />
      </div>

      {/* Notifications List */}
      <div className="px-4 py-4">
        {error ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <span className="text-xl text-red-600">⚠️</span>
            </div>
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              Error loading notifications
            </h3>
            <p className="mb-4 text-gray-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700"
            >
              Try Again
            </button>
          </div>
        ) : loading && (notifications || []).length === 0 ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-orange-200 border-t-orange-600"></div>
            <p className="text-gray-500">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-12 text-center">
            <BellIcon className="mx-auto mb-4 h-12 w-12 text-gray-300" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              {filter === 'unread'
                ? 'No unread notifications'
                : filter === 'read'
                  ? 'No read notifications'
                  : 'No notifications'}
            </h3>
            <p className="text-gray-500">
              {filter === 'unread'
                ? "You're all caught up!"
                : filter === 'read'
                  ? 'No read notifications yet'
                  : "You'll see your notifications here"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.notifications_id}
                className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md ${
                  notification.status === 'unread'
                    ? 'border-l-4 border-l-orange-500'
                    : ''
                }`}
                onClick={() => markAsRead(notification.notifications_id)}
              >
                <div className="flex items-start space-x-3">
                  {/* Notification Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="mb-2 text-sm text-gray-600">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(notification.timestamp)
                            .toLocaleString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false
                            })
                            .replace(',', '')}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {notification.status === 'unread' && (
                          <div className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-orange-500"></div>
                        )}
                        <div onClick={(e) => e.stopPropagation()}>
                          <NotificationActionMenu
                            notificationId={notification.notifications_id}
                            onDelete={deleteNotification}
                            isDeleting={isDeleting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="mt-6 text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
